import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageIcon from '@mui/icons-material/Image';

import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor, useSlideEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentHkcsQ1, PropsWithPreview, reposition, Size, SlideComponent } from '@/types/slide';
import { Box } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { ComponentLabel } from '../../ComponentLabel';
import { ContextualBar } from '../../ContextualBar';
import { useRightClickMenu } from '../../hooks/useRightClickMenu';
import { useSlideComponent } from '../../hooks/useSlideComponent';
import { RightClickMenu } from '../../RightClickMenu';
import { SlideRef } from '../../Slide';
import { EditPopover } from './EditPopover';

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentHkcsQ1: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    // provider and hooks
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { selectedSlide } = useSlideEditor();
    const { updateComponent, deleteComponent, createSlideImage, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();
    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    // ui state
    const { isSelected } = useSlideComponent(ref, [], !!preview);
    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);
    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);
    const [aspectRatio, setAspectRatio] = useState(model.size.width / model.size.height);
    const [editorPickerAnchorEl, setEditorPickerAnchorEl] = useState<HTMLElement | null>(null);

    const isEditing = isFocus || isSelected;

    const onUpdateComponent = React.useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onDeleteComponent = React.useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUploadImage = React.useCallback(
        async (file: File): Promise<string> => {
            try {
                return await createSlideImage(selectedSlide.editorData?.id ?? '', file);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
                return '';
            }
        },
        [createSlideImage, notifications, selectedSlide.editorData?.id],
    );

    const onBringToFrontHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = React.useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview && slideRef.current?.containerRef.current) {
            setEditorPickerAnchorEl(slideRef.current.containerRef.current);
        }
    }, [slideRef, isFocus, preview]);

    if (!isSlideComponentHkcsQ1(model)) {
        console.error(`[SlideComponentHkcsQ1] ${JSON.stringify(model)} is not SlideComponentHkcsQ1 model`);
        return null;
    }

    const isPlaceholder = !model.content.image1URL && !model.content.image1;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id='hkcsQ1-component'
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={() => {
                if (preview) return;

                if (slideRef.current?.containerRef.current) {
                    setEditorPickerAnchorEl(slideRef.current.containerRef.current);
                }
            }}
        >
            <ResizeBorder
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                aspectRatio={aspectRatio}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                '#hkcsQ1-component:hover &': {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                    >
                        {t('slide.legacy.hkcsQ1.title')}
                    </ComponentLabel>
                )}

                <Box
                    aria-label={'hkcsQ1-component'}
                    sx={{
                        m: 'inherit',
                        p: 'inherit',
                        width: '100%',
                        height: '100%',
                        aspectRatio: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {isPlaceholder ? (
                        <Box
                            sx={{
                                backgroundColor: (theme) => (theme.vars || theme).palette.grey[200],
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <ImageIcon sx={{ color: (theme) => (theme.vars || theme).palette.grey[400], width: '60%', height: '60%' }} />
                        </Box>
                    ) : (
                        <Box
                            component='img'
                            src={model.content.image1URL ? model.content.image1URL : `/asset/characters/${model.content.image1}/image.png`}
                            sx={{
                                width: '100%',
                                height: '100%',
                                aspectRatio: 'auto',
                            }}
                            width={model.size.width}
                        />
                    )}
                </Box>

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <EditIcon />,
                                tooltip: t('slide.legacy.character.tooltip.edit'),
                                onClick: () => {
                                    if (slideRef.current?.containerRef.current) {
                                        setEditorPickerAnchorEl(slideRef.current.containerRef.current);
                                    }
                                },
                            },
                        ]}
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.legacy.character.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}
            </ResizeBorder>

            {editorPickerAnchorEl && (
                <EditPopover
                    anchorEl={editorPickerAnchorEl}
                    value={model}
                    onClose={() => {
                        setEditorPickerAnchorEl(null);
                    }}
                    onUpdate={(model) => {
                        model = reposition(model);

                        setTPos(model.pos);
                        setTSize(model.size);
                        setAspectRatio(model.size.width / model.size.height);
                        setModel(model);
                        onUpdateComponent(model.id, model);
                    }}
                    onUploadImage={onUploadImage}
                />
            )}

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
